import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Briefcase, TrendingUp, FileCheck, ChevronRight, DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrency as fmtCur } from '@/lib/market';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const { dealStageMap } = useAppData();
  const [stats, setStats] = useState({
    totalDeals: 0,
    totalStartups: 0,
    pendingApprovals: 0,
    totalInvested: 0
  });
  const [recentDeals, setRecentDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const dealsRes = await axios.get(`${API}/deals/`);
      const deals = dealsRes.data || [];

      const startupsRes = await axios.get(`${API}/startups/companies`);
      const startups = startupsRes.data || [];

      const approvalsRes = await axios.get(`${API}/approvals/requests/pending`);
      const approvals = approvalsRes.data || [];

      const totalInvested = deals.reduce((sum, d) => sum + (d.target_investment_amount || 0), 0);

      setStats({
        totalDeals: deals.length,
        totalStartups: startups.length,
        pendingApprovals: approvals.length,
        totalInvested
      });

      setRecentDeals(deals.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => fmtCur(amount, user?.market);

  const getStageColor = (stage) => dealStageMap[stage]?.data?.color || 'bg-[#a8a8a8]';

  return (
    <div className="p-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#1a1a1a]">Dashboard</h1>
        <p className="text-[#4a4a4a] mt-1">Welcome back, {user?.name}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <Card className="bg-white border-[#e5e5e5] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#4a4a4a] uppercase tracking-wide truncate">Active Deals</p>
                <p className="text-2xl font-bold text-[#1a1a1a] mt-1">{stats.totalDeals}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-[#ffd87a]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#e5e5e5] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#4a4a4a] uppercase tracking-wide truncate">Pipeline Value</p>
                <p className="text-2xl font-bold text-[#1a1a1a] mt-1">{formatCurrency(stats.totalInvested)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-[#ffd87a]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Deals */}
      <div className="grid grid-cols-2 gap-6">
        <Card className="bg-white border-[#e5e5e5] shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#1a1a1a] text-lg">Recent Deals</CardTitle>
              <Link to="/deals" className="text-xs text-[#1a1a1a] hover:text-[#1a1a1a] flex items-center gap-1">
                View all <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-[#f5f5f5] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : recentDeals.length === 0 ? (
              <p className="text-[#4a4a4a] text-sm text-center py-8">No deals yet</p>
            ) : (
              <div className="space-y-3">
                {recentDeals.map((deal) => (
                  <Link
                    key={deal.id}
                    to={`/deals/${deal.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-[#f5f0e8] border border-transparent hover:border-[#e5e5e5] transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full ${getStageColor(deal.stage)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1a1a1a] truncate">{deal.deal_name}</p>
                      <p className="text-xs text-[#4a4a4a]">{deal.company_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#1a1a1a]">{formatCurrency(deal.target_investment_amount || 0)}</p>
                      <p className="text-xs text-[#4a4a4a] capitalize">{deal.stage?.replace(/_/g, ' ')}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-white border-[#e5e5e5] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#1a1a1a] text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link
              to="/deals"
              className="flex items-center gap-3 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:border-[#ffd87a] hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#ffd87a]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1a1a1a]">View Deal Pipeline</p>
                <p className="text-xs text-[#4a4a4a]">Track deals from sourcing to close</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#4a4a4a] ml-auto" />
            </Link>

            <Link
              to="/startups"
              className="flex items-center gap-3 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:border-[#ffd87a] hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#ffd87a]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1a1a1a]">Portfolio Companies</p>
                <p className="text-xs text-[#4a4a4a]">View startup investments</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#4a4a4a] ml-auto" />
            </Link>

            <Link
              to="/approvals"
              className="flex items-center gap-3 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:border-[#ffd87a] hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#ffd87a]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1a1a1a]">Pending Approvals</p>
                <p className="text-xs text-[#4a4a4a]">Review and approve requests</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#4a4a4a] ml-auto" />
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
